import { z } from 'zod'

export type DiagnosticLevel = 'info' | 'warn' | 'error'
export type DiagnosticFields = Record<string, unknown>

export interface DiagnosticEvent {
  timestampMs: number
  level: DiagnosticLevel
  tag: string
  msg: string
  fields: DiagnosticFields
}

export const DIAGNOSTIC_EVENT_MAX_BYTES = 8 * 1024
export const DIAGNOSTIC_FAILURE_MAX_BYTES = 32 * 1024

const encoder = new TextEncoder()
export const diagnosticEventSchema = z.strictObject({
  timestampMs: z.number().int().nonnegative(),
  level: z.enum(['info', 'warn', 'error']),
  tag: z.string().min(1).max(80),
  msg: z.string().min(1).max(200),
  fields: z.record(z.string(), z.unknown())
})
export const utilityDiagnosticSchema = z.strictObject({
  type: z.literal('diagnostic'),
  event: diagnosticEventSchema
})
const machineValue = /^[a-zA-Z0-9][a-zA-Z0-9_.:/-]*$/
const stringFields = new Set([
  'reqId',
  'requestId',
  'operationId',
  'sessionId',
  'activityId',
  'userId',
  'novelId',
  'traceId',
  'spanId',
  'parentId',
  'spanType',
  'channel',
  'processType',
  'appVersion',
  'runtimeVersion',
  'release',
  'environment',
  'model',
  'modelId',
  // 厂商音色 ID，如音色设计后没删掉的那个，便于人工清理。
  'voiceId',
  'provider',
  'runtime',
  'apiProtocol',
  'functionId',
  'aiFunctionId',
  'routeId',
  'moduleName',
  'tier',
  'effort',
  'domain',
  'modelKind',
  'operation',
  'event',
  'stage',
  'phase',
  'outcome',
  'status',
  'errorCode',
  'code',
  'name',
  'syscall',
  'failure',
  'failureKind',
  'reason',
  'source',
  'finishReason',
  'stopReason',
  'cancelSource',
  'method',
  'queue',
  'jobKind',
  'kind',
  'priority',
  'thinkingMode',
  'providerErrorCode',
  'upstreamRequestId',
  'clientRequestId',
  'cfRay',
  'tag',
  'msg',
  'level',
  'label',
  'messageCode',
  'fieldsDropped',
  'lastFailureCode',
  'tried',
  'billingDisposition',
  'settlementStatus',
  'signal',
  'format',
  'backend',
  'nativeRequestId',
  'modelRequestId',
  'localLlmErrorCode',
  'generationFailureReason',
  'diagnosticCode',
  'terminalReason',
  'schemaPath',
  'cancellationSource',
  'route',
  'transport',
  'rootType',
  'authPath',
  'accountPath',
  'walletPath',
  'modelTimeoutSource',
  'accountStatus',
  'aspectRatio',
  'assetSection',
  'attempt',
  'billingStatus',
  'command',
  'commandType',
  'counter',
  'createKind',
  'diagnosis',
  'direction',
  'dispatchEffort',
  'downloadMode',
  'eventName',
  'eventType',
  'executionEffort',
  'focusTarget',
  'grantKind',
  'group',
  'limit',
  'mutationType',
  'namespace',
  'paidFeatureId',
  'platform',
  'pressure',
  'purpose',
  'reserveCandidatePolicy',
  'result',
  'strategy',
  'subtype',
  'syntaxError',
  'toPackage',
  'toolId',
  'userTier',
  'window',
  'section',
  'arch',
  'locale',
  'qualityTier',
  'mode',
  'engine',
  'promptId',
  'packageId',
  'subscriptionStatus',
  'mimeType',
  'category',
  'error'
])
const contentKey =
  /^(?:prompt|system|user|assistant|messages?|input|output|content|body|bodySample|raw|response|requestPayload|responsePayload|clientResult|arguments?|parameters?|params|text|query|url|title|filename|filePath|novelPath|email|authorization|cookie|password|token|apiKey|secret|credential|providerContext|novelaideProviderContext|reasoning|thinking|signature|data)$/i

export function diagnosticByteLength(value: string): number {
  return encoder.encode(value).byteLength
}

function machineString(value: unknown, maxLength = 160): string | undefined {
  if (typeof value !== 'string' || value.length === 0 || value.length > maxLength) return undefined
  if (
    !machineValue.test(value) ||
    /^(?:sk-|sess-|Bearer)|^[^.]{16,}\.[^.]{16,}\.[^.]{16,}$/i.test(value)
  )
    return undefined
  return value
}

function diagnosticFrame(value: string): string | undefined {
  const location =
    /(?:^|[\s(@/])((?:apps\/(?:desktop|gateway)\/src\/|out\/(?:main|preload|renderer)\/|node_modules\/|node:)[^\s()?]*)(?:\?[^\s()]*)?:(\d+):(\d+)\)?$/.exec(
      value
    )
  const devLocation =
    /https?:\/\/(?:localhost|127\.0\.0\.1|\[::1\]):\d+\/src\/([^\s()?]*)(?:\?[^\s()]*)?:(\d+):(\d+)\)?$/.exec(
      value
    )
  const frame = location
    ? `${location[1]}:${location[2]}:${location[3]}`
    : devLocation
      ? `apps/desktop/src/renderer/src/${devLocation[1]}:${devLocation[2]}:${devLocation[3]}`
      : undefined
  return frame && frame.length <= 300 && !frame.includes('..') ? frame : undefined
}

/** Project metadata before serialization; custom getters, content and error messages are excluded. */
export function boundedDiagnosticFields(
  fields: DiagnosticFields | undefined,
  maxBytes = DIAGNOSTIC_EVENT_MAX_BYTES - 512
): DiagnosticFields {
  let visited = 0
  let truncated = false
  let redactedFields = 0
  let contextDropped = 0
  const seen = new WeakSet<object>()

  function project(value: unknown, key: string, depth: number): unknown {
    if (depth === 1 && key === 'truncated' && typeof value === 'boolean') return value
    if (
      depth === 1 &&
      ['contextDropped', 'redactedFields'].includes(key) &&
      typeof value === 'number' &&
      Number.isFinite(value)
    )
      return value
    if (depth > 5 || visited++ >= 256) {
      truncated = true
      return undefined
    }
    if (value == null) return undefined
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
    if (typeof value === 'bigint') {
      return value >= BigInt(Number.MIN_SAFE_INTEGER) && value <= BigInt(Number.MAX_SAFE_INTEGER)
        ? Number(value)
        : undefined
    }
    if (typeof value === 'string') {
      const safe =
        key === 'frames'
          ? diagnosticFrame(value)
          : stringFields.has(key)
            ? machineString(value)
            : key === 'path' && /^\/(?:v1|admin|auth|webhooks)(?:\/[-a-zA-Z0-9_.]+)*$/.test(value)
              ? value.slice(0, 200)
              : undefined
      if (safe === undefined) {
        redactedFields++
        if (value.length > 160) truncated = true
      }
      return safe
    }
    if (typeof value !== 'object') return undefined
    if (seen.has(value)) {
      truncated = true
      return undefined
    }
    seen.add(value)
    if (value instanceof Error || key === 'err' || key === 'cause')
      return projectError(value, depth)
    if (Array.isArray(value)) {
      if (value.length > 16) truncated = true
      if (key === 'contextEvents' && depth === 1) {
        // Spend the projection budget on the closest events first, then restore chronology.
        const events: unknown[] = []
        for (const item of value.slice(-16).reverse()) {
          if (visited >= 256) break
          const projected = project(item, key, depth + 1)
          if (visited > 256) break
          if (projected !== undefined) events.unshift(projected)
        }
        contextDropped += value.length - events.length
        if (contextDropped) truncated = true
        return events
      }
      return value
        .slice(0, 16)
        .map((item) => project(item, key, depth + 1))
        .filter((item) => item !== undefined)
    }
    return projectRecord(value, depth)
  }

  function projectRecord(value: object, depth: number): DiagnosticFields {
    const result: DiagnosticFields = {}
    const keys = Object.keys(value)
    // Root failure identity/cause/counters must not lose their budget to the flight recorder.
    if (depth === 0)
      keys.sort((a, b) => Number(a === 'contextEvents') - Number(b === 'contextEvents'))
    if (keys.length > 64) truncated = true
    for (const key of keys.slice(0, 64)) {
      if (
        !/^[a-zA-Z][a-zA-Z0-9_]{0,79}$/.test(key) ||
        contentKey.test(key) ||
        /(?:authorization|cookie|password|secret|credential|apiKey|accessToken|refreshToken|tokenIds|embeddings?|vectors?)$/i.test(
          key
        )
      ) {
        redactedFields++
        continue
      }
      const descriptor = Object.getOwnPropertyDescriptor(value, key)
      if (!descriptor || !('value' in descriptor)) {
        redactedFields++
        continue
      }
      if (
        depth === 0 &&
        key === 'contextEvents' &&
        Array.isArray(descriptor.value) &&
        visited >= 256
      ) {
        contextDropped += descriptor.value.length
        result.contextEvents = []
        truncated = true
        continue
      }
      const item = project(descriptor.value, key, depth + 1)
      if (item !== undefined) result[key] = item
    }
    return result
  }

  function projectError(value: object, depth: number): DiagnosticFields {
    const result: DiagnosticFields = {}
    for (const key of [
      'name',
      'code',
      'errorCode',
      'syscall',
      'status',
      'errno',
      'retryable',
      'localLlmErrorCode',
      'generationFailureReason',
      'diagnosticCode'
    ]) {
      const item = dataProperty(value, key)
      const safe = project(item, key, depth + 1)
      if (safe !== undefined) result[key] = safe
    }
    if (!result.name) result.name = value instanceof Error ? 'Error' : 'ThrownValue'
    const cause = dataProperty(value, 'cause')
    if (cause !== undefined) result.cause = project(cause, 'cause', depth + 1)
    const errors = dataProperty(value, 'errors')
    if (Array.isArray(errors)) {
      if (errors.length > 8) truncated = true
      result.errors = errors.slice(0, 8).map((error) => project(error, 'cause', depth + 1))
    }
    const stack = dataProperty(value, 'stack')
    if (typeof stack === 'string') {
      // Only application/dependency source locations; omit messages, URLs and author file paths.
      const frames = stack
        .split('\n')
        .slice(1, 17)
        .flatMap((line) => {
          const location = diagnosticFrame(line)
          return location ? [location] : []
        })
      if (frames.length) result.frames = project(frames, 'frames', depth + 1)
    }
    const projectedFrames = dataProperty(value, 'frames')
    if (Array.isArray(projectedFrames))
      result.frames = project(projectedFrames, 'frames', depth + 1)
    return result
  }

  let result: DiagnosticFields
  try {
    result = projectRecord(fields ?? {}, 0)
  } catch {
    return { fieldsDropped: 'projection_failed' }
  }
  if (redactedFields) result.redactedFields = redactedFields
  if (contextDropped)
    result.contextDropped =
      (typeof result.contextDropped === 'number' ? result.contextDropped : 0) + contextDropped
  if (truncated) result.truncated = true
  // Correlation and structured errors survive optional detail overflow.
  const essential = new Set([
    'reqId',
    'requestId',
    'operationId',
    'modelRequestId',
    'upstreamRequestId',
    'clientRequestId',
    'traceId',
    'spanId',
    'channel',
    'stage',
    'phase',
    'errorCode',
    'status',
    'contextDropped',
    'truncated',
    'err'
  ])
  if (result.err && diagnosticByteLength(JSON.stringify(result)) > maxBytes) {
    const compactError = (value: unknown, depth = 0): unknown => {
      if (!value || typeof value !== 'object' || depth > 3) return undefined
      const error = value as DiagnosticFields
      const compact: DiagnosticFields = {}
      for (const key of [
        'name',
        'code',
        'errorCode',
        'status',
        'syscall',
        'errno',
        'retryable',
        'localLlmErrorCode',
        'generationFailureReason',
        'diagnosticCode'
      ])
        if (error[key] !== undefined) compact[key] = error[key]
      if (error.cause) compact.cause = compactError(error.cause, depth + 1)
      if (Array.isArray(error.errors))
        compact.errors = error.errors.slice(0, 2).map((item) => compactError(item, depth + 1))
      return compact
    }
    result.err = compactError(result.err)
    result.truncated = true
  }
  if (Array.isArray(result.contextEvents)) {
    while (result.contextEvents.length && diagnosticByteLength(JSON.stringify(result)) > maxBytes) {
      result.contextEvents.shift()
      result.contextDropped =
        (typeof result.contextDropped === 'number' ? result.contextDropped : 0) + 1
      result.truncated = true
    }
  }
  for (const key of Object.keys(result).filter((key) => !essential.has(key))) {
    if (diagnosticByteLength(JSON.stringify(result)) <= maxBytes) break
    delete result[key]
    result.truncated = true
  }
  for (const key of [
    'channel',
    'spanId',
    'traceId',
    'upstreamRequestId',
    'clientRequestId',
    'modelRequestId',
    'phase',
    'stage',
    'operationId',
    'requestId',
    'status',
    'errorCode',
    'reqId',
    'err'
  ]) {
    if (diagnosticByteLength(JSON.stringify(result)) <= maxBytes) break
    delete result[key]
    result.truncated = true
  }
  return result
}

function dataProperty(value: object, key: string): unknown {
  for (
    let current: object | null = value, depth = 0;
    current && depth < 5;
    current = Object.getPrototypeOf(current), depth++
  ) {
    const descriptor = Object.getOwnPropertyDescriptor(current, key)
    if (descriptor) {
      if ('value' in descriptor) return descriptor.value
      // V8 exposes even an assigned Error.stack through a native accessor. Never call user getters.
      if (
        key === 'stack' &&
        value instanceof Error &&
        descriptor.get &&
        Function.prototype.toString.call(descriptor.get).includes('[native code]')
      ) {
        return descriptor.get.call(value)
      }
      return undefined
    }
  }
  return undefined
}

export function createDiagnosticEvent(
  level: DiagnosticLevel,
  tag: string,
  msg: string,
  fields?: DiagnosticFields,
  timestampMs = Date.now()
): DiagnosticEvent {
  return {
    timestampMs,
    level,
    tag: machineString(tag, 80) ?? 'diagnostic',
    msg: machineString(msg, 200) ?? 'unclassified_event',
    fields: boundedDiagnosticFields(
      fields,
      (level === 'info' ? DIAGNOSTIC_EVENT_MAX_BYTES : DIAGNOSTIC_FAILURE_MAX_BYTES) - 512
    )
  }
}

/** Byte-bounded flight recorder for already projected events, independent of disk transports. */
export class DiagnosticBuffer {
  private entries: Array<{ event: DiagnosticEvent; bytes: number }> = []
  private bytes = 0
  private dropped = 0

  constructor(
    private readonly maxBytes = 4 * 1024 * 1024,
    private readonly maxEvents = 4096
  ) {}

  add(event: DiagnosticEvent): void {
    const bytes = diagnosticByteLength(JSON.stringify(event))
    if (bytes > this.maxBytes) {
      this.dropped++
      return
    }
    this.entries.push({ event, bytes })
    this.bytes += bytes
    while (this.bytes > this.maxBytes || this.entries.length > this.maxEvents) {
      this.bytes -= this.entries.shift()!.bytes
      this.dropped++
    }
  }

  recent(
    operationId?: string,
    maxBytes = 24 * 1024
  ): { events: DiagnosticEvent[]; dropped: number } {
    const events: DiagnosticEvent[] = []
    let bytes = 0
    let dropped = this.dropped
    for (let index = this.entries.length - 1; index >= 0; index--) {
      const entry = this.entries[index]!
      if (
        operationId &&
        !['operationId', 'requestId', 'reqId', 'activityId'].some(
          (key) => entry.event.fields[key] === operationId
        )
      )
        continue
      if (bytes + entry.bytes > maxBytes || events.length >= 32) {
        dropped++
        continue
      }
      bytes += entry.bytes
      events.unshift(entry.event)
    }
    return { events, dropped }
  }
}
